import React, { createContext, useContext, useEffect, ReactNode, useState } from 'react';
import Keycloak from 'keycloak-js';
import keycloakInstance from './keycloak';
import { Spinner } from 'react-bootstrap';
import logo from './assets/images/logo-df.png';

interface KeycloakContextType {
  keycloak: Keycloak;
  initialized: boolean;
  isAuthenticated: boolean;
  username: string | null;
  name: string | null;
  profilePhotoUrl: string | null;
  tenantId: string | null;
  userId: string | null;
  email: string | null;
  roles: string[] | null;
}

const KeycloakContext = createContext<KeycloakContextType | undefined>(undefined);

export const useKeycloak = (): KeycloakContextType => {
  const context = useContext(KeycloakContext);
  if (!context) {
    throw new Error('useKeycloak must be used within a KeycloakProvider');
  }
  return context;
};

// Helper function to check if token is expired or about to expire
const isTokenExpired = (token: string, buffer: number = 60): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    return exp - now < buffer * 1000; // Check if expires within buffer seconds
  } catch (error) {
    console.error('Error parsing token:', error);
    return true; // Consider invalid tokens as expired
  }
};

// Helper function to refresh token via backend
const refreshTokenViaBackend = async (refreshToken: string): Promise<{ access_token: string; refresh_token: string } | null> => {
  try {
    const backendApiUrl = process.env.REACT_APP_API_BASE_URL;
    const response = await fetch(`${backendApiUrl}/api/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refresh_token: refreshToken,
      }),
    });

    if (!response.ok) {
      console.error('Token refresh failed:', response.status);
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Error refreshing token:', error);
    return null;
  }
};

export const KeycloakProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [keycloakInstanceState, setKeycloakInstance] = useState<Keycloak>(keycloakInstance);
  const [initialized, setInitialized] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [username, setUsername] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [roles, setRoles] = useState<string[] | null>(null);

  // Proactive token refresh - refresh token 5 minutes before expiry for extended sessions
  useEffect(() => {
    if (!isAuthenticated || !keycloakInstanceState.token) return;

    const checkAndRefreshToken = async () => {
      try {
        // Try to update token if it expires in less than 5 minutes (300 seconds)
        // This allows for longer sessions without interruption
        const refreshed = await keycloakInstanceState.updateToken(300);
        
        if (refreshed) {
          //// console.log('✅ Token proactively refreshed');
          localStorage.setItem('keycloakToken', keycloakInstanceState.token || '');
          localStorage.setItem('keycloakRefreshToken', keycloakInstanceState.refreshToken || '');
        }
      } catch (error) {
        //console.error('Failed to proactively refresh token:', error);
        // If refresh fails, try via backend
        const storedRefreshToken = localStorage.getItem('keycloakRefreshToken');
        if (storedRefreshToken) {
          const refreshResult = await refreshTokenViaBackend(storedRefreshToken);
          if (refreshResult) {
            keycloakInstanceState.token = refreshResult.access_token;
            keycloakInstanceState.refreshToken = refreshResult.refresh_token;
            localStorage.setItem('keycloakToken', refreshResult.access_token);
            localStorage.setItem('keycloakRefreshToken', refreshResult.refresh_token);
            //// console.log('✅ Token refreshed via backend');
          } else {
            // Both refresh methods failed - logout
            handleLogout();
          }
        }
      }
    };

    // Check every 5 minutes (300000ms) for better performance and extended sessions
    // This reduces unnecessary refresh attempts while maintaining active sessions
    const interval = setInterval(checkAndRefreshToken, 300000);

    return () => clearInterval(interval);
  }, [isAuthenticated, keycloakInstanceState]);

  const handleLogout = () => {
    //// console.log('🔴 Logging out due to refresh failure');
    localStorage.removeItem('keycloakToken');
    localStorage.removeItem('keycloakRefreshToken');
    localStorage.removeItem('user_id');
    localStorage.removeItem('tenant_id');
    keycloakInstanceState.authenticated = false;
    setIsAuthenticated(false);
    window.location.href = '/login';
  };

  const loadUserProfile = async () => {
    try {
      const userProfile = await keycloakInstanceState.loadUserProfile();
      //// console.log("📋 User Profile loaded:", userProfile);
      
      setUsername(userProfile.username ?? null);
      setEmail(userProfile.email ?? null);
      setUserId(userProfile.id ?? null);
      
      const fullName = `${userProfile.firstName ?? ''} ${userProfile.lastName ?? ''}`.trim();
      setName(fullName.length > 0 ? fullName : null);
      
      const extractedTenantId = userProfile.attributes?.tenant_id 
        ? Array.isArray(userProfile.attributes.tenant_id) 
          ? userProfile.attributes.tenant_id[0] 
          : null 
        : null;

      setTenantId(extractedTenantId);
      
      // Fetch Roles from Token
      const decodedToken: any = keycloakInstanceState.tokenParsed;
      setRoles(decodedToken.realm_access?.roles || []);
      
      // Store in localStorage for persistence
      localStorage.setItem("user_id", userProfile.username ?? "");
      localStorage.setItem("tenant_id", extractedTenantId ?? "");
    } catch (error) {
      //console.error('Error loading user profile:', error);
      throw error;
    }
  };

  useEffect(() => {
    const initKeycloak = async () => {
      try {
        // Check if we have stored tokens from backend authentication
        let storedToken = localStorage.getItem('keycloakToken');
        let storedRefreshToken = localStorage.getItem('keycloakRefreshToken');

        let authenticated = false;

        // If we have tokens, check if the access token is expired
        if (storedToken && storedRefreshToken) {
          //// console.log('📄 Found stored tokens');
          
          // Check if access token is expired or about to expire
          if (isTokenExpired(storedToken)) {
            //// console.log('⚠️ Stored access token is expired, attempting refresh...');
            
            // Try to refresh the token via backend before initializing Keycloak
            const refreshResult = await refreshTokenViaBackend(storedRefreshToken);
            
            if (refreshResult) {
              //// console.log('✅ Token refreshed successfully via backend');
              storedToken = refreshResult.access_token;
              storedRefreshToken = refreshResult.refresh_token;
              localStorage.setItem('keycloakToken', storedToken);
              localStorage.setItem('keycloakRefreshToken', storedRefreshToken);
            } else {
              //// console.log('❌ Token refresh failed, clearing tokens');
              localStorage.removeItem('keycloakToken');
              localStorage.removeItem('keycloakRefreshToken');
              storedToken = null;
              storedRefreshToken = null;
            }
          }
        }

        if (storedToken && storedRefreshToken) {
          // Initialize with (possibly refreshed) stored tokens
          //// console.log('🔄 Initializing Keycloak with valid tokens');
          authenticated = await keycloakInstanceState.init({
            token: storedToken,
            refreshToken: storedRefreshToken,
            onLoad: 'check-sso',
            pkceMethod: 'S256',
            checkLoginIframe: false,
          });
        } else {
          // Normal SSO check (for users who logged in via Keycloak directly)
          //// console.log('🔄 Checking SSO authentication status');
          authenticated = await keycloakInstanceState.init({
            onLoad: 'check-sso',
            pkceMethod: 'S256',
            checkLoginIframe: false,
          });
        }

        if (authenticated) {
          //// console.log('✅ Authenticated successfully');
          // Update stored tokens
          localStorage.setItem('keycloakToken', keycloakInstanceState.token || '');
          localStorage.setItem('keycloakRefreshToken', keycloakInstanceState.refreshToken || '');
          
          await loadUserProfile();
        } else {
          //// console.log('❌ Not authenticated');
          // Clear any stale tokens
          localStorage.removeItem('keycloakToken');
          localStorage.removeItem('keycloakRefreshToken');
        }

        setIsAuthenticated(authenticated);
        setInitialized(true);

        // Handle token expiration during active session
        keycloakInstanceState.onTokenExpired = async () => {
          try {
            //// console.log('🔄 Token expired during session, refreshing...');
            // Allow 5 minutes buffer for token refresh to maintain long sessions
            await keycloakInstanceState.updateToken(300);
            localStorage.setItem('keycloakToken', keycloakInstanceState.token || '');
            localStorage.setItem('keycloakRefreshToken', keycloakInstanceState.refreshToken || '');
            //// console.log('✅ Token refreshed successfully');
          } catch (error) {
            //console.error("❌ Token refresh failed", error);
            // Try backend refresh as fallback
            const storedRefreshToken = localStorage.getItem('keycloakRefreshToken');
            if (storedRefreshToken) {
              const refreshResult = await refreshTokenViaBackend(storedRefreshToken);
              if (refreshResult) {
                keycloakInstanceState.token = refreshResult.access_token;
                keycloakInstanceState.refreshToken = refreshResult.refresh_token;
                localStorage.setItem('keycloakToken', refreshResult.access_token);
                localStorage.setItem('keycloakRefreshToken', refreshResult.refresh_token);
                //// console.log('✅ Token refreshed via backend fallback');
              } else {
                handleLogout();
              }
            } else {
              handleLogout();
            }
          }
        };

      } catch (error) {
        //console.error('❌ Failed to initialize Keycloak:', error);
        // Clear potentially corrupted tokens
        localStorage.removeItem('keycloakToken');
        localStorage.removeItem('keycloakRefreshToken');
        setInitialized(true);
      }
    };

    if (!initialized) {
      initKeycloak();
    }
  }, [initialized, keycloakInstanceState]);

  return (
    <KeycloakContext.Provider value={{ 
      keycloak: keycloakInstanceState, 
      initialized, 
      isAuthenticated, 
      username, 
      name, 
      profilePhotoUrl, 
      tenantId, 
      userId, 
      email,  
      roles 
    }}>
      {initialized ? children : (
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: '12px' }}>
          <img
            src={logo}
            alt="Loading"
            style={{ width: '200px', height: 'auto' }}
          />
          <p style={{ fontSize: '1rem', letterSpacing: '0.05em', color: '#6c757d', margin: 0 }}>
            Your Digital Co-worker
          </p>
          <div style={{ marginTop: '16px' }}>
            <Spinner animation="grow" variant="primary" />
          </div>
        </div>
      )}
    </KeycloakContext.Provider>
  );
};